import type { CoverLetterDto, UpdateCoverLetterDto } from "@reactive-resume/dto";
import { useMutation } from "@tanstack/react-query";
import type { AxiosResponse } from "axios";
import debounce from "lodash.debounce";

import { axios } from "@/client/libs/axios";
import { queryClient } from "@/client/libs/query-client";
import { useNotificationStore } from "@/client/stores/useNotification";
import { useSaveStatusStore } from "@/client/stores/useSaveStatusStore";

let resetSavedTimeout: ReturnType<typeof setTimeout> | null = null;

export const updateCoverLetter = async (data: UpdateCoverLetterDto) => {
  try {
    useSaveStatusStore.getState().setStatus("saving");
    const response = await axios.patch<
      CoverLetterDto,
      AxiosResponse<CoverLetterDto>,
      UpdateCoverLetterDto
    >(`/cover-letter/${data.id}`, data);

    queryClient.setQueryData<CoverLetterDto>(
      ["cover-letter", { id: response.data.id }],
      response.data,
    );

    queryClient.setQueryData<CoverLetterDto[]>(["cover-letters"], (cache) => {
      if (!cache) return [response.data];
      return cache.map((cl) => (cl.id === response.data.id ? response.data : cl));
    });

    useSaveStatusStore.getState().setStatus("saved");
    if (resetSavedTimeout) clearTimeout(resetSavedTimeout);
    resetSavedTimeout = setTimeout(() => {
      if (useSaveStatusStore.getState().status === "saved") {
        useSaveStatusStore.getState().setStatus("idle");
      }
    }, 2000);

    return response.data;
  } catch (err: any) {
    useSaveStatusStore.getState().setStatus("error");
    useNotificationStore.getState().showError(err?.message || "An error occurred");
    throw err;
  }
};

export const debouncedUpdateCoverLetter = debounce(updateCoverLetter, 500);

export const useUpdateCoverLetter = () => {
  const {
    error,
    isPending: loading,
    mutateAsync: updateCoverLetterFn,
  } = useMutation({
    mutationFn: updateCoverLetter,
  });

  return { updateCoverLetter: updateCoverLetterFn, loading, error };
};
